import {useEffect, useRef, useCallback} from 'react';

const useScrollRestore = (key, elementRef) => {
  const rafId = useRef(null);
  const restoredRef = useRef(false);
  const storageKey = `scroll-pos-${key}`;

  const savePosition = useCallback(() => {
    if (rafId.current) return;
    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      const scrollX = elementRef?.current?.scrollLeft ?? 0;
      const scrollY = window.scrollY ?? 0;
      sessionStorage.setItem(storageKey, JSON.stringify({x: scrollX, y: scrollY}));
    });
  }, [storageKey, elementRef]);

  // Save: listen to scroll events on window (Y) and element (X)
  useEffect(() => {
    const el = elementRef?.current;

    window.addEventListener('scroll', savePosition, {passive: true});
    if (el) {
      el.addEventListener('scroll', savePosition, {passive: true});
    }

    return () => {
      window.removeEventListener('scroll', savePosition);
      if (el) {
        el.removeEventListener('scroll', savePosition);
      }
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, [savePosition, elementRef]);

  // Restore: use ResizeObserver to detect when content is large enough
  useEffect(() => {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return;

    let target;
    try {
      target = JSON.parse(raw);
    } catch (e) {
      return;
    }

    restoredRef.current = false;

    const tryRestore = () => {
      const el = elementRef?.current;
      if (!el) return false;

      const canScrollX = el.scrollWidth > el.clientWidth && el.scrollWidth >= target.x;
      const canScrollY = document.documentElement.scrollHeight >= target.y + window.innerHeight;

      if (canScrollX || target.x === 0) {
        el.scrollLeft = target.x;
      }
      if (canScrollY || target.y === 0) {
        window.scrollTo(0, target.y);
      }

      // Consider restored when both axes are reachable
      return (canScrollX || target.x === 0) && (canScrollY || target.y === 0);
    };

    // Try immediately
    if (tryRestore()) {
      restoredRef.current = true;
      return;
    }

    // Observe content size changes via ResizeObserver on the scrollable child
    const el = elementRef?.current;
    const observeTarget = el?.firstElementChild || el;
    if (!observeTarget) return;

    const observer = new ResizeObserver(() => {
      if (restoredRef.current) return;
      if (tryRestore()) {
        restoredRef.current = true;
        observer.disconnect();
      }
    });

    observer.observe(observeTarget);

    // Safety timeout: stop trying after 5s
    const timeout = setTimeout(() => {
      if (!restoredRef.current) {
        tryRestore();
      }
      observer.disconnect();
    }, 5000);

    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [storageKey, elementRef]);
};

export default useScrollRestore;
